/**
 * Deployment-shape constants shared by every package.
 *
 * The app is served from the root of its host (`https://prismengine.cloud/`,
 * the desktop app's `http://127.0.0.1:7860/`). URLs the browser builds still go
 * through `basePath()` so a mount point could come back in one place.
 */

/**
 * The Prism website (game library, accounts). The desktop app publishes games
 * and signs in here; change it in this one place if the site moves.
 */
export const PRISM_WEBSITE = 'https://prismengine.cloud';

/** Public mount point of the app. Empty: the root. No trailing slash. */
export const BASE_PATH = '';

/** Where the app was mounted before (old links redirect from here). */
export const LEGACY_BASE_PATH = '/ai-team';

/** Append `BASE_PATH` to a server-relative path. Idempotent for already-prefixed input. */
export function basePath(path = ''): string {
  if (!path) return BASE_PATH || '/';
  if (BASE_PATH && (path === BASE_PATH || path.startsWith(`${BASE_PATH}/`))) return path;
  return `${BASE_PATH}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Absolute URL for an API route, relative to the app mount point. */
export function apiUrl(path: string): string {
  return basePath(`/api${path.startsWith('/') ? path : `/${path}`}`);
}

/** WebSocket endpoint for the event stream. */
export function wsUrl(): string {
  // `location` is absent under Node, so this package stays free of DOM lib types
  // and can be imported by the server, the worker and the browser alike.
  const loc = (globalThis as { location?: { protocol: string; host: string } }).location;
  if (!loc) return `ws://127.0.0.1:7860${basePath('/ws')}`;
  const scheme = loc.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${loc.host}${basePath('/ws')}`;
}

/**
 * WebSocket endpoint for interactive terminals.
 *
 * Deliberately separate from the event socket: the event channel fans out to
 * every client, a terminal is a private byte stream for one browser tab.
 */
export function ptyUrl(): string {
  const loc = (globalThis as { location?: { protocol: string; host: string } }).location;
  if (!loc) return `ws://127.0.0.1:7860${basePath('/pty')}`;
  const scheme = loc.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${loc.host}${basePath('/pty')}`;
}

/** Loopback-only bind. The internet can only reach this through the reverse proxy. */
export const DEFAULT_HOST = '127.0.0.1';
export const DEFAULT_PORT = 7860;

/** Idle heartbeat. Defeats the 900s reverse-proxy idle timeout that returns 520. */
export const HEARTBEAT_MS = 30_000;

/**
 * No single HTTP request may run longer than this before responding.
 * The proxy has a ~125s read timeout; anything slower becomes a 524. Long agent
 * runs return a task id immediately and stream progress over the WebSocket.
 */
export const MAX_REQUEST_MS = 30_000;